import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useUser } from "../lib/UserContext";

interface ClientOption {
  id: string;
  raison_sociale: string;
}

interface PraticienForm {
  nom: string;
  email?: string;
  ical_url?: string;
}

interface CalendarEntry {
  id: string;
  client_id: string;
  type: "google" | "ical" | "plato";
  praticiens: PraticienForm[];
  raison_sociale: string;
}

export default function Calendars() {
  const user = useUser();
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [profiles, setProfiles] = useState<ClientOption[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [calendarType, setCalendarType] = useState<"google" | "ical" | "plato">("ical");
  const [calendarCount, setCalendarCount] = useState<number>(1);
  const [praticiens, setPraticiens] = useState<PraticienForm[]>([{ nom: "" }]);
  const [calendars, setCalendars] = useState<CalendarEntry[]>([]);
  const [statusMessages, setStatusMessages] = useState<{ [id: string]: string }>({});
  const [platoBookingLink, setPlatoBookingLink] = useState<string>("");
  const [platoApiKey, setPlatoApiKey] = useState<string>("");

  useEffect(() => {
    if (user?.role !== "admin") return;
    loadClients();
    loadProfiles();
  }, [user]);

  useEffect(() => {
    if (clients.length > 0) {
      loadCalendars();
    }
  }, [clients]);

  const loadClients = async () => {
    const { data } = await supabase.from("clients").select("id, raison_sociale");
    setClients(data || []);
  };

  const loadProfiles = async () => {
    const { data, error } = await supabase.from("profiles").select("id");
    if (!error && data) setProfiles(data);
  };

  const loadCalendars = async () => {
    const { data, error } = await supabase.from("calendars").select("*");
    if (!error && data) {
      const enriched = data.map((c: any) => {
        const client = clients.find(cl => cl.id === c.client_id);
        return {
          ...c,
          raison_sociale: client?.raison_sociale || "❓ Inconnu"
        };
      });
      setCalendars(enriched);
    }
  };

  const handleCountChange = (count: number) => {
    setCalendarCount(count);
    const updated = Array.from({ length: count }, (_, i) => praticiens[i] || { nom: "" });
    setPraticiens(updated);
  };

  const handleChangePraticien = (index: number, field: keyof PraticienForm, value: string) => {
    const updated = [...praticiens];
    updated[index] = { ...updated[index], [field]: value };
    setPraticiens(updated);
  };

  const handleCreateCalendar = async () => {
    if (!selectedClientId || praticiens.some(p => !p.nom)) {
      return alert("Tous les noms doivent être renseignés");
    }
    
    if (calendarType === "google" && praticiens.some(p => !p.email)) {
      return alert("Tous les emails doivent être renseignés");
    }
    
    if (calendarType === "ical" && praticiens.some(p => !p.ical_url)) {
      return alert("Tous les liens iCal doivent être renseignés");
    }
    
    if (calendarType === "plato") {
      if (!platoBookingLink || !platoApiKey) {
        return alert("Pour Plato, le booking link et l'API key doivent être renseignés");
      }
    }

    const { error } = await supabase.from("calendars").insert({
      client_id: selectedClientId,
      type: calendarType,
      praticiens: praticiens,
      plato_booking_link: calendarType === "plato" ? platoBookingLink : null,
      plato_api_key: calendarType === "plato" ? platoApiKey : null,
    });
    if (!error) {
      setSelectedClientId("");
      setCalendarType("ical");
      setCalendarCount(1);
      setPraticiens([{ nom: "" }]);
      setPlatoBookingLink("");
      setPlatoApiKey("");
      loadCalendars();
    }
  };

  const handleGenerateCalendar = async (clientId: string) => {
    setStatusMessages((prev) => ({ ...prev, [clientId]: "⏳ Génération en cours…" }));
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/generate-calendar/${clientId}`, {
        method: "POST",
      });
      const data = await res.json();
      if (res.ok) {
        setStatusMessages((prev) => ({ ...prev, [clientId]: `✅ ${data.message}` }));
      } else {
        setStatusMessages((prev) => ({ ...prev, [clientId]: `❌ ${data.detail || "Erreur inconnue"}` }));
      }
    } catch (err) {
      setStatusMessages((prev) => ({ ...prev, [clientId]: `❌ Erreur de connexion` }));
    }
  };

  const groupedCalendars = calendars.reduce((acc: { [key: string]: CalendarEntry[] }, curr) => {
    if (!acc[curr.raison_sociale]) acc[curr.raison_sociale] = [];
    acc[curr.raison_sociale].push(curr);
    return acc;
  }, {});

  if (!user) return <div className="text-center py-10">Chargement…</div>;
  if (user.role !== "admin") return <div className="text-center py-10 text-gray-600">⛔ Accès réservé aux administrateurs.</div>;

  return (
    <div className="max-w-4xl mx-auto py-8">
      <h1 className="text-2xl font-bold mb-6">📅 Calendars management</h1>

      <div className="mb-4">
        <label className="block mb-1 font-medium">Customer</label>
        <select
          value={selectedClientId}
          onChange={(e) => setSelectedClientId(e.target.value)}
          className="w-full border px-3 py-2 rounded"
        >
          <option value="">-- Choose a customer --</option>
          {clients.map((p) => (
            <option key={p.id} value={p.id}>{p.raison_sociale}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block mb-1 font-medium">Calendar type</label>
          <select
            value={calendarType}
            onChange={(e) => setCalendarType(e.target.value as "google" | "ical" | "plato")}
            className="w-full border px-3 py-2 rounded"
          >
            <option value="ical">iCal</option>
            <option value="google">Google Calendar</option>
            <option value="plato">Plato</option>
          </select>
        </div>
        <div>
          <label className="block mb-1 font-medium">Number of practitioners</label>
          <input
            type="number"
            min={1}
            value={calendarCount}
            onChange={(e) => handleCountChange(Number(e.target.value))}
            className="w-full border px-3 py-2 rounded"
          />
        </div>
      </div>

      {praticiens.map((p, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
          <input
            placeholder="Practitioner name"
            value={p.nom}
            onChange={(e) => handleChangePraticien(i, "nom", e.target.value)}
            className="border px-3 py-2 rounded"
          />
          {calendarType === "google" && (
            <input
              placeholder="Email Google"
              value={p.email || ""}
              onChange={(e) => handleChangePraticien(i, "email", e.target.value)}
              className="border px-3 py-2 rounded"
            />
          )}
          
          {calendarType === "ical" && (
            <input
              placeholder="ICS link (.ics)"
              value={p.ical_url || ""}
              onChange={(e) => handleChangePraticien(i, "ical_url", e.target.value)}
              className="border px-3 py-2 rounded"
            />
          )}
          
          {calendarType === "plato" && (
            <div className="mb-4 space-y-3">
              <div>
                <label className="block mb-1 font-medium">Plato DB Name</label>
                <input
                  type="text"
                  placeholder="ex: drdentalforte"
                  value={platoBookingLink}
                  onChange={(e) => setPlatoBookingLink(e.target.value)}
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
              <div>
                <label className="block mb-1 font-medium">Plato API key</label>
                <input
                  type="text"
                  placeholder="Plato API key"
                  value={platoApiKey}
                  onChange={(e) => setPlatoApiKey(e.target.value)}
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
            </div>
          )}
        </div>
      ))}

      <button onClick={handleCreateCalendar} className="mt-2 bg-primary text-white px-6 py-2 rounded">
        Valider l'agenda
      </button>

      <h2 className="text-lg font-semibold mt-10 mb-4">Saved calendars</h2>
      <div className="space-y-6">
        {Object.entries(groupedCalendars).map(([raison, agendaList]) => (
          <div key={raison}>
            <h3 className="text-md font-bold mb-2">{raison}</h3>
            {agendaList.map((c) => (
              <div key={c.id} className="border p-4 rounded shadow bg-white mb-4">
                <p className="font-semibold">Client ID : {c.client_id}</p>
                <p>Type : {c.type}</p>
                <p>Practitioner :</p>
                <ul className="list-disc ml-6">
                  {c.praticiens.map((p: any, idx: number) => (
                    <li key={idx}>{p.nom} – {c.type === "google" ? p.email : p.ical_url}</li>
                  ))}
                </ul>
                <button
                  onClick={() => handleGenerateCalendar(c.client_id)}
                  className="mt-2 text-sm text-blue-600 underline"
                >
                  Generate the calendar
                </button>
                {statusMessages[c.client_id] && (
                  <p className="text-sm mt-1 text-gray-700">{statusMessages[c.client_id]}</p>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
